import {
	ReactElement,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import classnames from 'classnames';

import Calendar from './Calendar';
import { useCAWContext } from '../CAW';
import { getRTL, getUniqueId, translate } from '@micro-apps/env';
import { useBlurDetector, useWindowSize } from '@fshr/hooks';
import Dialog from '../../dialog';
import { format } from 'date-fns/format';
import { createPortal } from 'react-dom';

export default function DateField(): ReactElement<any> {
	const { isSingleProperty, planStore, isPPC, locale, dateFnsLocale, isDesktop, isHeroVariant } =
		useCAWContext();
	const [width, height] = useWindowSize();
	const isTallViewport = isHeroVariant && (height || 0) >= 1024;
	const calendarField = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);
	const portalRef = useRef<HTMLDivElement>(null);
	const anchorRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const usesPopup = isDesktop;

	const closeDialog = useCallback((options?: { restoreFocus?: boolean }) => {
		setIsOpen(false);
		if (options?.restoreFocus) {
			requestAnimationFrame(() => {
				triggerRef.current?.focus({ preventScroll: true });
			});
		}
	}, []);
	const closeDialogFromEvent = useCallback(() => {
		closeDialog();
	}, [closeDialog]);
	const closeAndRestoreFocus = useCallback(() => {
		closeDialog({ restoreFocus: true });
	}, [closeDialog]);

	const openDialog = useCallback(() => {
		if (isOpen) return;
		window.dispatchEvent(new Event('closeCAWDialogs'));
		setIsOpen(true);
	}, [isOpen]);
	const closeDialogOnBlur = useCallback(() => {
		if (usesPopup) {
			return;
		}
		closeDialog();
	}, [closeDialog, usesPopup]);
	useBlurDetector(calendarField, isOpen, closeDialogOnBlur);

	useLayoutEffect(() => {
		if (!isOpen) return;
		if (usesPopup) {
			const el = anchorRef.current;
			if (!el || !portalRef.current) return;
			if (!isHeroVariant) {
				portalRef.current.style.top = '100%';
				portalRef.current.style.left = getRTL() ? 'auto' : '0';
				portalRef.current.style.right = getRTL() ? '0' : 'auto';
				return;
			}
			const anchorPos = el.getBoundingClientRect();
			const offsetFromTop = anchorPos.top - (document.body?.getBoundingClientRect()?.top || 0);
			portalRef.current.style.top = `${offsetFromTop}px`;
			portalRef.current.style.left = `${anchorPos.left}px`;
			if (isHeroVariant && containerRef.current) {
				containerRef.current.style.maxHeight = `${offsetFromTop + 40}px`;
			}
			return;
		}
		const calendar = containerRef.current?.querySelector('.Calendar') as HTMLDivElement | null;
		if (calendar && containerRef.current) {
			const calRect = calendar.getBoundingClientRect();
			if (calRect.bottom > height) {
				containerRef.current.style.top = `${height - calRect.bottom + 40}px`;
			}
		}
	}, [height, isOpen, usesPopup, isHeroVariant, planStore?.fetching]);

	useEffect(() => {
		window.addEventListener('closeCAWDialogs', closeDialogFromEvent);
		if (isHeroVariant) {
			window.addEventListener('scroll', closeDialogFromEvent);
		}
		return () => {
			window.removeEventListener('closeCAWDialogs', closeDialogFromEvent);
		};
	}, [closeDialogFromEvent]);

	useEffect(() => {
		if (!usesPopup) {
			return;
		}
		const onPointerDownOutside = (event: MouseEvent | TouchEvent) => {
			if (!isOpen) {
				return;
			}
			const target = event.target as Node | null;
			const dialogEl = containerRef.current;
			if (!target || !calendarField.current || !dialogEl) {
				return;
			}
			const dialogWrapper = dialogEl.querySelector('.Calendar-dialog');
			const triggerEl = triggerRef.current;
			const clickedInsideDialog = !!dialogWrapper && dialogWrapper.contains(target);
			const clickedOnTrigger = !!triggerEl && triggerEl.contains(target);

			// Close whenever the click is outside the visible popup panel and outside the trigger.
			if (!clickedInsideDialog && !clickedOnTrigger) {
				closeDialog();
			}
		};
		document.addEventListener('mousedown', onPointerDownOutside);
		document.addEventListener('touchstart', onPointerDownOutside);
		return () => {
			document.removeEventListener('mousedown', onPointerDownOutside);
			document.removeEventListener('touchstart', onPointerDownOutside);
		};
	}, [closeDialog, isOpen, usesPopup]);

	useEffect(() => {
		if (!usesPopup || !isOpen) {
			return;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			closeDialog({ restoreFocus: true });
		};
		document.addEventListener('keydown', onKeyDown, true);
		return () => document.removeEventListener('keydown', onKeyDown, true);
	}, [closeDialog, isOpen, usesPopup]);

	const containerID = useMemo(
		() => `Calendar-container-${getUniqueId()}${isPPC ? '-ppc' : ''}`,
		[isPPC]
	);
	const dialogId = useMemo(() => `Calendar-${getUniqueId()}`, []);
	const dateFieldAnchorId = `${containerID}-calendar-anchor`;
	const portalRootId = `${containerID}-portal`;

	useEffect(() => {
		if (!isOpen) return;
		window.dispatchEvent(new Event('openDatesDialog'));
	}, [isOpen]);

	const screenReaderSummaryText =
		planStore?.search?.checkinDate && planStore?.search?.checkoutDate
			? `${translate('Selected Dates')} ${format(
					planStore?.search?.checkinDate,
					'MMMM d yyyy'
				)} to ${format(planStore?.search?.checkoutDate, 'MMMM d yyyy')}${
					planStore?.search.flexDatesSelected
						? ` | ± ${planStore?.search.flexDatesSelected} ${translate('days')}`
						: ''
				}`
			: translate('Select Dates for Check-in and Check-out');

	const labelClasses = classnames(
		'DateField_label DateField_label--active m-0 opacity-100 h-auto w-auto transition-none text-label',
		{
			'static transform-none translate-y-0 mb-[6px]': isPPC,
			static: !isPPC,
		}
	);

	return (
		<div
			tabIndex={-1}
			className={classnames(
				'Calendar-container Calendar-root relative flex flex-col spacing-stack-2xs justify-end',
				{
					active: isOpen,
					'col w-[330px]': !isHeroVariant && !isPPC && !isTallViewport,
					singleProperty: isSingleProperty && !isPPC,
					'col-lg-12 w-full p-0': isPPC,
					'col w-full max-w-[330px] xl:max-w-full xl:min-w-[330px]':
						isHeroVariant && !isTallViewport,
				}
			)}
			id={containerID}
			ref={calendarField}
		>
			{isDesktop && !isHeroVariant && !isPPC ? (
				// Needed on desktop (not PPC or hero) so the CAW stays open when scrolling down
				<div className="Calendar-row" hidden aria-hidden="true" />
			) : null}
			<label
				htmlFor="datefield-id"
				id="datefield-label-id"
				className={labelClasses}
				aria-label={screenReaderSummaryText}
				aria-live="polite"
			>
				<span className={isHeroVariant ? 'text-text-inverse-heading' : 'text-text-secondary'}>
					{translate('Check in')} — {translate('Check out')}
				</span>
			</label>
			<div
				ref={anchorRef}
				id={dateFieldAnchorId}
				className={classnames('relative', {
					'w-full': isPPC || isTallViewport,
					'w-full max-w-[330px] min-w-[240px]': !isPPC && !isTallViewport,
					'xl:max-w-full xl:min-w-[330px]': isHeroVariant && !isTallViewport,
				})}
			>
				<div
					className={classnames('DateField_row text-body3', {
						'block w-full': isPPC,
						'w-full': isPPC || isTallViewport,
						'w-full max-w-[330px] min-w-[240px]': !isPPC && !isTallViewport,
						'xl:max-w-full xl:min-w-[330px]': isHeroVariant && !isTallViewport,
					})}
				>
					<div
						id="datefield-id"
						ref={triggerRef}
						className={classnames('DateField relative w-full', {
							active: planStore?.search?.checkinDate || planStore?.search?.checkoutDate,
						})}
						onClick={openDialog}
						onFocus={usesPopup ? undefined : openDialog}
						onKeyDown={e => {
							if (usesPopup && (e.key === 'Enter' || e.key === ' ')) {
								e.preventDefault();
								openDialog();
							}
						}}
						role="button"
						tabIndex={0}
					>
						<div className="relative">
							<div
								className={classnames(
									'DateField_container relative flex h-10 w-full items-center pr-8 pl-[10px] leading-none whitespace-nowrap overflow-hidden text-ellipsis text-[16px]',
									{
										'pl-8 pr-[10px]': getRTL(),
										'shows-options': isOpen,
										'bg-white w-full': isPPC && !isHeroVariant,
										'bg-surface-form': !isPPC && !isHeroVariant,
										'text-text-secondary border-border-subtle': !isHeroVariant,
										'text-text-inverse-heading border-border-inverse bg-img-overlay-40 backdrop-blur-[15px]':
											isHeroVariant,
									},
									'border-[1px] rounded-[4px]'
								)}
							>
								{planStore?.search?.checkinDate || planStore?.search?.checkoutDate ? (
									<div
										className={classnames(
											'DateField_wrap',
											{
												'DateField_wrap--active':
													planStore?.search?.checkinDate || planStore?.search?.checkoutDate,
											},
											'overflow-hidden whitespace-nowrap text-ellipsis leading-none pr-[15px] pt-0'
										)}
									>
										<span className="screen-reader-only">{screenReaderSummaryText}</span>
										<span
											className="DateField_input DateField_input--active py-0 leading-none text-body3"
											aria-hidden="true"
										>
											<span
												className={
													isHeroVariant ? 'text-text-inverse-heading' : 'text-text-secondary'
												}
											>
												<span className="text-[16px]">
													{planStore?.search?.checkinDate &&
														format(planStore?.search?.checkinDate, 'P', {
															locale: dateFnsLocale?.localeMappings[locale || 'en'],
														})}
												</span>
											</span>
										</span>
										<span
											className="DateField_input DateField_input--active py-0 leading-none text-body3"
											aria-hidden="true"
										>
											<span
												className={
													isHeroVariant ? 'text-text-inverse-heading' : 'text-text-secondary'
												}
											>
												<span className="text-[16px]"> - </span>
											</span>
										</span>
										<span
											className="DateField_input DateField_input--active py-0 leading-none text-body3"
											aria-hidden="true"
										>
											<span
												className={
													isHeroVariant ? 'text-text-inverse-heading' : 'text-text-secondary'
												}
											>
												<span className="text-[16px]">
													{planStore?.search?.checkoutDate &&
														format(planStore?.search?.checkoutDate, 'P', {
															locale: dateFnsLocale?.localeMappings[locale || 'en'],
														})}
												</span>
											</span>
										</span>
										{planStore?.search.flexDatesSelected ? (
											<span
												className="DateField_input DateField_input--active py-0 leading-none text-body3"
												aria-hidden="true"
											>
												<span
													className={
														isHeroVariant ? 'text-text-inverse-heading' : 'text-text-secondary'
													}
												>
													<span className="text-[16px]">
														{` | ± ${planStore?.search.flexDatesSelected} ${translate('days')}`}
													</span>
												</span>
											</span>
										) : undefined}
									</div>
								) : null}

								{!planStore?.search?.checkinDate && !planStore?.search?.checkoutDate ? (
									<p
										className="DateField_placeholder my-0 py-0 leading-none text-body3"
										aria-hidden="true"
									>
										<span
											className={
												isHeroVariant ? 'text-text-inverse-heading' : 'text-text-secondary'
											}
										>
											<span className="text-[16px]">
												{translate('Check in')} - {translate('Check out')}
											</span>
										</span>
									</p>
								) : undefined}
								<div className="Calendar-apply" />
							</div>
							<span
								aria-hidden="true"
								className={classnames(
									'pointer-events-none absolute right-3 top-1/2 h-[8px] w-[8px] -translate-y-1/2 rotate-45 border-r border-b',
									{
										'right-[unset] left-3': getRTL(),
										'border-text-primary': !isHeroVariant,
										'border-text-inverse-heading': isHeroVariant,
									}
								)}
							/>
						</div>
					</div>
				</div>
			</div>

			{createPortal(
				<div
					id={portalRootId}
					className="tailwind-root CAW"
					ref={portalRef}
					style={{ position: 'absolute' }}
				>
					<Dialog
						isOpen={isOpen}
						onClose={closeAndRestoreFocus}
						title=""
						id={dialogId}
						disablePortal
						containerRef={containerRef}
						classNames={{
							container: classnames(
								'Calendar-container',
								'absolute left-0 z-[1001] overflow-visible',
								{
									'left-[unset] right-0': getRTL(),
									'top-[100%]': !isHeroVariant,
									'-bottom-[40px] bg-surface-raised': isHeroVariant,
								}
							),
							overlay: 'hidden',
							dialog: 'Calendar-dialog',
						}}
						contentClassName={classnames(
							'Calendar-content Calendar-wrapper Calendar-row relative bg-surface-raised border border-border-subtle',
							{
								'shadow-raised': !usesPopup,
								'shadow-[0_5px_15px_0_rgba(3,3,3,0.12)]': usesPopup,
							}
						)}
					>
						<Calendar onClose={closeAndRestoreFocus} openDialog={openDialog} />
					</Dialog>
				</div>,
				anchorRef.current && !isHeroVariant ? anchorRef.current : document.body
			)}
		</div>
	);
}
